import { Component, type ErrorInfo, type ReactNode } from "react";

type ErrorBoundaryProps = {
  fallback?: ReactNode | ((error: Error) => ReactNode);
  children: ReactNode;
};

type ErrorBoundaryState = {
  error: Error | null;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (typeof this.props.fallback === "function") {
      return this.props.fallback(error);
    }

    return (
      this.props.fallback ?? (
        <div className="rounded-md border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          <p className="font-medium">Something went wrong</p>
          <p className="mt-1 text-red-500">{error.message}</p>
        </div>
      )
    );
  }
}
